import { useMemo } from 'react';
import { useFiles, type FileMeta } from './use-files';
import { useDocuments, type DocumentMeta } from './use-documents';

/**
 * Merges user-uploaded files (user_files table) and agent-generated memory
 * documents (memory_documents table) into one list view. Storage stays
 * separate; the union happens client-side so the unrelated /files CRUD
 * paths don't have to know memory documents exist.
 */
export type UnifiedFileItem =
  | ({ source: 'upload'; sortDate: string } & FileMeta)
  | {
      source: 'document';
      sortDate: string;
      filename: string;
      title: string;
      date: string;
      tags: string[];
      summary: string;
      codingSessionId: string | null;
      createdAt: string;
      updatedAt: string;
      trashedAt?: string | null;
    };

/**
 * Merge upload + document rows into one sorted list.
 *
 * FRONT-2392: the Files page itself no longer calls this — its grid (both
 * tabs) reads the server-paged/server-searched `useFilesOverview` instead
 * (`hooks/queries/use-files-overview.ts`). `useUnifiedFiles` is still the
 * shared file picker's (`~/components/file-picker.tsx`) data source, which
 * deliberately kept the old full-download-then-merge shape (out of scope
 * for that ticket).
 */
function mergeUnified(files: FileMeta[], docs: DocumentMeta[]): UnifiedFileItem[] {
  const uploads: UnifiedFileItem[] = files.map((f) => ({
    ...f,
    source: 'upload' as const,
    sortDate: f.trashedAt ?? f.updatedAt,
  }));
  const documents: UnifiedFileItem[] = docs.map((d) => ({
    source: 'document' as const,
    sortDate: d.trashedAt ?? d.updatedAt,
    filename: d.filename,
    title: d.title,
    date: d.date,
    tags: d.tags,
    summary: d.summary,
    codingSessionId: d.codingSessionId ?? null,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    trashedAt: d.trashedAt ?? null,
  }));
  return [...uploads, ...documents].sort((a, b) => b.sortDate.localeCompare(a.sortDate));
}

/**
 * `enabled` (FRONT-1085) defaults to `true`. The shared file picker
 * (`~/components/file-picker.tsx`, moved out of `features/work/` by
 * FRONT-776 once the Folders feature needed it too — now this hook's only
 * caller, see the module docblock above) passes `enabled: isOpen` instead,
 * deferring the fetch until it's actually opened — same convention as
 * `useWorkBoard`/`ContainerPicker`.
 */
export function useUnifiedFiles(opts?: { enabled?: boolean }) {
  const files = useFiles(opts);
  const docs = useDocuments(opts);

  const items = useMemo<UnifiedFileItem[]>(
    () => mergeUnified(files.data ?? [], docs.data ?? []),
    [files.data, docs.data],
  );

  return {
    items,
    isLoading: files.isLoading || docs.isLoading,
    error: files.error ?? docs.error,
  };
}
